import _ from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useScrollToTopOnBookingChange from '../../../hooks/useScrollToTopOnBookingChange';
import Drawer from '@mui/material/Drawer';
import CollapseText from '../../general/CollapseText.react';
import { Button, IconButton, Snackbar } from '@mui/material';
import { ReactComponent as CopyIcon } from '../../../img/bookings/copy.svg';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import BookingStatus from '../../../utils/BookingStatus';
import { Catering } from 'arbitrip-common/client/utils';
import BookingField from '../new-bookings/BookingDrawerField.react';
import BookingDrawerReservationSummary from '../new-bookings/BookingDrawerReservationSummary.react';
import { ReactComponent as BreakfastIcon } from '../../../img/amenities/breakfast@3x.svg';
import { getBreakfastIncluded, isRequestActionable, isReservationAvailableToApprove } from './helpers';
import ReservationConstants from '../../../constants/ReservationConstants';
import { RTL } from 'arbitrip-common/translation/const';

const date_format = 'D MMM YYYY';

function getTravelersText(reservation) {
	let travelers_text = reservation.travelers.map((t) => `${t.first_name} ${t.last_name}`).join(', ');
	if (reservation.deal.details.children_count > 0) {
		travelers_text += `\r\n+${reservation.deal.details.children_count} ${reservation.deal.details.children_count > 1 ? 'Children' : 'Child'}`;
	}
	return travelers_text;
}

const RequestDrawer = ({ open, onClose, booking, profile, handleApprove, handleDecline }) => {
	const { t, i18n } = useTranslation();
	const [copyFlash, setCopyFlash] = useState(false);

	const reservation = _.get(booking, 'reservation');
	const ticket = _.get(booking, 'request_ticket');

	const is_approver = _.get(ticket, 'approver._id') === profile.id;

	const cancellation_policy_text = useMemo(() => {
		const multiLanguageTexts = _.get(reservation, 'deal.dca.cancellationPoliciesMultiLanguageTexts');
		const defaultText = _.get(reservation, 'deal.dca.cancellationPoliciesText');
		return multiLanguageTexts ? multiLanguageTexts[i18n.language] : defaultText;
	}, [booking, i18n.language]);

	const breakfastIncluded = useMemo(() => {
		return getBreakfastIncluded(reservation);
	}, [reservation]);

	const copyReservationId = () => {
		setCopyFlash(true);
		navigator.clipboard.writeText(_.get(reservation, 'short_id'));

		setTimeout(() => {
			setCopyFlash(false);
		}, 4000);
	};

	const drawerRef = useScrollToTopOnBookingChange(booking);

	if (!reservation) {
		console.log('RequestDrawer ::: NO BOOKING');
		return null;
	}

	const isRTL = i18n.dir() === RTL;

	return (
		<React.Fragment>
			<Drawer
				anchor="right"
				open={open}
				onClose={onClose}
				className="booking-drawer"
				variant="persistent"
				SlideProps={{
					appear: true,
				}}
				sx={{
					'& .MuiDrawer-paper': {
						direction: isRTL ? 'rtl' : 'ltr',
						left: isRTL ? '0 !important' : 'auto',
						right: isRTL ? 'auto' : '0 !important',
						transform: 'none !important',
					},
				}}
			>
				<div ref={drawerRef} tabIndex="-1" className="booking-drawer-inner">
					<div className="booking-title">
						<div className="flex space-between align-items-baseline">
							<div className="trip-to">
								<span className="trip-to-hotel-name">{reservation.hotel.name}</span>
							</div>
							<IconButton className="close-drawer-button" aria-label="close" onClick={onClose}>
								<CloseOutlined />
							</IconButton>
						</div>
						<div className="city">
							<div>{reservation.hotel.city}</div>
							<div>{`${reservation.hotel.address}, ${reservation.hotel.city}, ${reservation.hotel.country}`}</div>
						</div>
						<div className={`booking-status ${ticket.status}`}>
							<span>● {BookingStatus.getRequestStatus(ticket.status)}</span>
						</div>
					</div>

					{(profile.isApprovalFlowPowerUser() || is_approver) && isRequestActionable(ticket) && (
						<div className="actions">
							{isReservationAvailableToApprove(reservation.status) ? (
								<Button
									variant="outlined"
									fullWidth
									color="success"
									onClick={() => handleApprove(booking)}
								>
									{t('Approve')}
								</Button>
							) : (
								<Button
									variant="outlined"
									fullWidth
									color="success"
									onClick={() => window.open(ticket.action_url, '_blank')}
								>
									{t('Book')}
								</Button>
							)}

							<Button variant="outlined" fullWidth onClick={() => handleDecline(booking)}>
								{t('Decline')}
							</Button>
						</div>
					)}

					{!_.isEmpty(ticket.meta.reason) && (
						<BookingField fieldTitle={t('RequestReason')} fieldValue={ticket.meta.reason} />
					)}

					<BookingDrawerReservationSummary booking={reservation} />

					<BookingField
						fieldTitle={t('CancellationPolicy')}
						fieldValue={
							cancellation_policy_text ? (
								<CollapseText text={cancellation_policy_text} breakLines />
							) : (
								t('None')
							)
						}
					/>

					<BookingField
						fieldTitle={`${reservation.deal.details.room_count > 1 ? t('Rooms') : t('Room')}${reservation.deal.details.room_count > 1 ? ` (${reservation.deal.details.room_count}x)` : ''}`}
						fieldValue={
							<React.Fragment>
								{reservation.deal.details.room_description}
								{breakfastIncluded && (
									<div className="space">
										<BreakfastIcon />
										{Catering.getCatering(reservation.deal.dca.heuristicOptions)}
									</div>
								)}
							</React.Fragment>
						}
					/>

					<BookingField
						fieldTitle={`${t('Travelers')}${reservation.travelers.length + (reservation.deal.details.children_count || 0) > 1 ? `s (${reservation.travelers.length + (reservation.deal.details.children_count || 0)}x)` : ''}`}
						fieldValue={getTravelersText(reservation)}
					/>

					{reservation.short_id && (
						<BookingField
							fieldTitle={t('ReservationID')}
							fieldValue={reservation.short_id}
							icon={<CopyIcon />}
							onClick={copyReservationId}
							className={{ active: copyFlash }}
						/>
					)}

					{!_.isEmpty(ticket.approver.full_name) && (
						<BookingField fieldTitle={t('Approver')} fieldValue={ticket.approver.full_name} />
					)}

					{!_.isEmpty(reservation.creator) && (
						<BookingField fieldTitle={t('Creator')} fieldValue={reservation.creator.full_name} />
					)}

					<BookingField
						fieldTitle={t('CreationDate')}
						fieldValue={moment(reservation.creation_date).format(date_format)}
					/>
				</div>
			</Drawer>

			<Snackbar open={copyFlash} message={t('ReservationIDCopied')} autoHideDuration={4000} />
		</React.Fragment>
	);
};

export default RequestDrawer;
